import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, IconButton, Box,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GrassIcon from '@mui/icons-material/Grass';
import PetsIcon from '@mui/icons-material/Pets';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../AuthContext';

const navItems = [
  { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'Herd', path: '/herd', icon: <PetsIcon /> },
  { label: 'Receipts', path: '/receipts', icon: <ReceiptIcon /> },
  { label: 'Balances', path: '/balances', icon: <AccountBalanceIcon /> },
  { label: 'My Info', path: '/my-info', icon: <PersonIcon /> },
];

export default function NavBar() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ width: 220 }} onClick={() => setDrawerOpen(false)}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <GrassIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Wood Family Farm</Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
      {isAuthenticated && (
        <>
          <Divider />
          <List>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </List>
        </>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="sticky">
        <Toolbar>
          {isAuthenticated && isMobile && (
            <IconButton color="inherit" edge="start" sx={{ mr: 1 }} onClick={() => setDrawerOpen(true)} aria-label="Open navigation menu">
              <MenuIcon />
            </IconButton>
          )}
          <GrassIcon sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
            Wood Family Farm
          </Typography>
          {isAuthenticated && !isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{ fontWeight: location.pathname.startsWith(item.path) && item.path !== '/' ? 700 : 400 }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          {isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              {!isMobile && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>{user?.username}</Typography>
              )}
              <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />} aria-label={isMobile ? 'Logout' : undefined}>
                {isMobile ? '' : 'Logout'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" component={Link} to="/login">Login</Button>
              <Button color="inherit" component={Link} to="/register">Register</Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawerContent}
      </Drawer>
    </>
  );
}
